import type { TimelineElement } from '../../types/timelineElement'
import type { EduModule, EduNodeKind } from '../../types/schema'
import type { NlsKey } from '../../nls/strings_nls'
import type { NlsInterpolationValues } from '../../nls/nlsTypes'

export interface ParsedNameListResult {
  names: string[]
  duplicates: string[]
  clashesWithExisting: string[]
  isValid: boolean
}

/**
 * Parses a newline-separated list of names. Drops blank lines, trims each line,
 * and detects duplicates within the list and against existing sibling titles.
 */
export function parseNameList(
  input: string,
  existingSiblingTitles: string[] = [],
): ParsedNameListResult {
  const rawLines = input.split('\n')
  const names: string[] = []
  const seenInList = new Set<string>()
  const duplicates: string[] = []

  for (const line of rawLines) {
    const trimmed = line.trim()
    if (!trimmed) continue
    const lower = trimmed.toLowerCase()
    if (seenInList.has(lower)) {
      if (!duplicates.includes(trimmed)) {
        duplicates.push(trimmed)
      }
    } else {
      seenInList.add(lower)
      names.push(trimmed)
    }
  }

  const existingLowerSet = new Set(existingSiblingTitles.map((t) => t.trim().toLowerCase()))
  const clashesWithExisting: string[] = []
  for (const name of names) {
    if (existingLowerSet.has(name.toLowerCase())) {
      clashesWithExisting.push(name)
    }
  }

  const isValid =
    names.length > 0 && duplicates.length === 0 && clashesWithExisting.length === 0

  return {
    names,
    duplicates,
    clashesWithExisting,
    isValid,
  }
}

export interface AcademicDatesResult {
  startDate: string
  endDate: string
}

/**
 * Prefilled dates for academic year (teaching: 1 April - 31 March)
 * or training programme (training: today + 3 months).
 */
export function getDefaultAcademicDates(
  kind: 'TEACHING' | 'TRAINING',
  referenceDate = new Date(),
): AcademicDatesResult {
  const pad = (n: number) => String(n).padStart(2, '0')

  if (kind === 'TEACHING') {
    const year = referenceDate.getFullYear()
    const month = referenceDate.getMonth() // 0 = Jan, 2 = Mar, 3 = Apr
    let startYear: number
    let endYear: number

    if (month >= 3) {
      // April - December: academic year starts 1 April of this year
      startYear = year
      endYear = year + 1
    } else {
      // January - March: academic year started 1 April of previous year
      startYear = year - 1
      endYear = year
    }

    return {
      startDate: `${startYear}-04-01`,
      endDate: `${endYear}-03-31`,
    }
  }

  // TRAINING: today + 3 months
  const startYear = referenceDate.getFullYear()
  const startMonth = pad(referenceDate.getMonth() + 1)
  const startDay = pad(referenceDate.getDate())
  const startDate = `${startYear}-${startMonth}-${startDay}`

  const future = new Date(referenceDate)
  future.setMonth(future.getMonth() + 3)
  const endYear = future.getFullYear()
  const endMonth = pad(future.getMonth() + 1)
  const endDay = pad(future.getDate())
  const endDate = `${endYear}-${endMonth}-${endDay}`

  return {
    startDate,
    endDate,
  }
}

export interface EduClassNode {
  element: TimelineElement
  subjects: TimelineElement[]
}

export interface EduRootNode {
  element: TimelineElement
  classes: EduClassNode[]
}

export interface EduTreeResult {
  activeRoots: EduRootNode[]
  archivedRoots: EduRootNode[]
  adoptableGoals: TimelineElement[]
}

/**
 * Builds structured active and archived education trees from timeline elements.
 */
export function buildEduTree(
  elements: TimelineElement[],
  kind: 'TEACHING' | 'TRAINING',
): EduTreeResult {
  const moduleName: EduModule = kind === 'TEACHING' ? 'teaching' : 'training'
  const rootKind: EduNodeKind = kind === 'TEACHING' ? 'YEAR' : 'PROGRAMME'
  const classKinds: EduNodeKind[] = kind === 'TEACHING' ? ['CLASS'] : ['GROUP', 'CLIENT']
  const subjectKinds: EduNodeKind[] = kind === 'TEACHING' ? ['SUBJECT'] : ['PHASE']

  const activeRoots: EduRootNode[] = []
  const archivedRoots: EduRootNode[] = []

  const rootElements = elements.filter(
    (el) =>
      el.type === 'GOAL' &&
      el.edu?.module === moduleName &&
      el.edu?.kind === rootKind,
  )

  for (const rootEl of rootElements) {
    const isArchived = Boolean(rootEl.edu?.archivedYear)
    const classElements = elements.filter(
      (el) =>
        el.type === 'TASK' &&
        el.parentId === rootEl.id &&
        el.edu?.module === moduleName &&
        classKinds.includes(el.edu?.kind as EduNodeKind),
    )

    const classes: EduClassNode[] = classElements.map((classEl) => {
      const subjects = elements.filter(
        (el) =>
          el.type === 'SUBTASK' &&
          el.parentId === classEl.id &&
          el.edu?.module === moduleName &&
          subjectKinds.includes(el.edu?.kind as EduNodeKind),
      )
      return {
        element: classEl,
        subjects,
      }
    })

    const rootNode: EduRootNode = {
      element: rootEl,
      classes,
    }

    if (isArchived) {
      archivedRoots.push(rootNode)
    } else {
      activeRoots.push(rootNode)
    }
  }

  const adoptableGoals = elements.filter(
    (el) => el.type === 'GOAL' && !el.parentId && !el.edu,
  )

  return {
    activeRoots,
    archivedRoots,
    adoptableGoals,
  }
}

export interface CopySubjectsTargetPlan {
  targetClassId: string
  subjectsToCreate: string[]
  skippedCount: number
}

export interface CopySubjectsPlanResult {
  targets: CopySubjectsTargetPlan[]
  totalSkipped: number
}

/**
 * Plans copying subjects from one class to selected target classes,
 * skipping subjects that already exist on the target class.
 */
export function planCopySubjects(
  sourceSubjects: Array<{ title: string }>,
  targetClasses: Array<{
    id: string
    title: string
    existingSubjects: Array<{ title: string }>
  }>,
): CopySubjectsPlanResult {
  let totalSkipped = 0
  const targets: CopySubjectsTargetPlan[] = []

  for (const target of targetClasses) {
    const existingTitles = new Set(
      target.existingSubjects.map((s) => s.title.trim().toLowerCase()),
    )
    const subjectsToCreate: string[] = []
    let skippedCount = 0

    for (const sub of sourceSubjects) {
      const title = sub.title.trim()
      if (existingTitles.has(title.toLowerCase())) {
        skippedCount++
      } else {
        subjectsToCreate.push(title)
      }
    }

    totalSkipped += skippedCount
    targets.push({
      targetClassId: target.id,
      subjectsToCreate,
      skippedCount,
    })
  }

  return {
    targets,
    totalSkipped,
  }
}

/**
 * Extracts a normalized server error code from details.code first,
 * then bracketed [CODE] prefix of message, then error.code.
 */
export function eduServerErrorCode(err: unknown): string {
  if (!err) return ''
  const errorObj = err as {
    code?: string
    details?: { code?: string }
    message?: string
  }
  const detailsCode = errorObj?.details?.code
  if (typeof detailsCode === 'string' && detailsCode.trim()) {
    return detailsCode.trim().replace(/^functions\//, '')
  }
  if (typeof errorObj?.message === 'string') {
    const match = errorObj.message.match(/^\[([A-Z0-9_]+)\]/)
    if (match) {
      return match[1]
    }
  }
  if (typeof errorObj?.code === 'string' && errorObj.code.trim()) {
    return errorObj.code.trim().replace(/^functions\//, '')
  }
  return ''
}

/**
 * Maps server errors (HttpsError details.code or callable code) to NLS localized messages.
 * Never leaks raw server error message strings.
 */
export function mapEduServerError(
  err: unknown,
  t: (key: NlsKey, params?: NlsInterpolationValues) => string,
  learnerNameLookup?: ((id: string) => string) | Record<string, string>,
): string {
  const code = eduServerErrorCode(err)

  if (code === 'CONSENT_BLOCKS_TAG') {
    const details = (err as { details?: { learnerIds?: string[] } })?.details
    const ids = Array.isArray(details?.learnerIds) ? details.learnerIds : []
    const resolve = typeof learnerNameLookup === 'function'
      ? learnerNameLookup
      : (id: string) => (learnerNameLookup && typeof learnerNameLookup === 'object' ? (learnerNameLookup as Record<string, string>)[id] || id : id)
    const resolvedNames = ids.map(resolve).filter(Boolean).join(', ')
    return resolvedNames
      ? t('NLSEduErrorConsentBlocksTag', { names: resolvedNames })
      : t('NLSEduErrorConsentBlocksTagGeneric')
  }
  if (code === 'NODE_REQUIRED') {
    return t('NLSEduErrorNodeRequired')
  }
  if (code === 'PIN_LIMIT') {
    return t('NLSEduErrorPinLimit')
  }
  if (code === 'POST_NOT_VISIBLE') {
    return t('NLSEduErrorPostNotVisible')
  }

  if (code === 'SAME_LEVEL') {
    return t('NLSEduProgressPromoteSameLevelError')
  }
  if (code === 'TEMPLATE_NOT_FOUND') {
    return t('NLSEduCertTemplateNotFoundError')
  }
  if (code === 'TEMPLATE_ARCHIVED') {
    return t('NLSEduCertTemplateArchivedError')
  }
  if (code === 'INVALID_STATUS') {
    return t('NLSEduCertInvalidStatusError')
  }
  if (code === 'DATE_IN_FUTURE') {
    return t('NLSEduProgressRecordFutureDateError')
  }
  if (code === 'NOT_ENROLLED') {
    return t('NLSEduProgressRecordNotEnrolledError')
  }
  if (code === 'VALUE_NOT_PENDING') {
    return t('NLSEduProgressSelfReportsNotPending')
  }
  if (code === 'VERSION_CONFLICT') {
    return t('NLSEduProgressMetricsStaleVersion')
  }
  if (code === 'UNKNOWN_PLACEHOLDER') {
    const details = (err as { details?: { field?: 'title' | 'body'; placeholder?: string } })?.details
    const field = details?.field === 'title' ? 'title' : 'body'
    const placeholder = details?.placeholder ? `{${details.placeholder}}` : ''
    return t('NLSEduCertTemplateUnknownPlaceholder', { placeholder, field })
  }
  if (code === 'ARCHIVED_YEAR') {
    return t('NLSEducationBuilderErrorArchivedYear')
  }
  if (code === 'MODULE_OFF') {
    return t('NLSEducationBuilderErrorModuleOff')
  }
  if (code === 'PARENT_SCREEN_MODE_REGION') {
    return t('NLSEduPrivacyRegionRefused')
  }
  if (code === 'LEARNER_FROZEN') {
    return t('NLSEducationBuilderErrorLearnerFrozen')
  }
  if (code === 'NOT_IN_CLASS') {
    return t('NLSEducationBuilderErrorNotInClass')
  }
  if (code === 'ROLE_TOO_LOW') {
    return t('NLSEducationBuilderErrorRoleTooLow')
  }
  if (code === 'PROMOTION_MISSING') {
    return t('NLSEducationBuilderErrorPromotionMissing')
  }
  if (code === 'ROLLOVER_IN_PROGRESS') {
    return t('NLSEducationBuilderErrorRolloverInProgress')
  }
  if (code === 'ALREADY_ARCHIVED') {
    return t('NLSEducationBuilderErrorAlreadyArchived')
  }
  if (code === 'TOO_MANY_NODES') {
    return t('NLSEducationBuilderErrorTooManyNodes')
  }
  if (code === 'NOT_TIMETABLE_EDITOR') {
    return t('NLSEducationBuilderErrorNotTimetableEditor')
  }
  if (code === 'ROOM_NOT_FOUND') {
    return t('NLSEducationBuilderErrorRoomNotFound')
  }
  if (code === 'SERIES_NOT_FOUND') {
    return t('NLSEducationBuilderErrorSeriesNotFound')
  }
  if (code === 'SERIES_HAS_SESSIONS') {
    return t('NLSEducationBuilderErrorSeriesHasSessions')
  }
  if (code === 'NOT_TRAINING') {
    return t('NLSEducationSlotEditorErrorNotTraining')
  }
  if (code === 'INVALID_CAPACITY') {
    return t('NLSEducationSlotEditorErrorInvalidCapacity')
  }
  if (code === 'NOT_AN_OCCURRENCE') {
    return t('NLSEducationBuilderErrorNotAnOccurrence')
  }
  if (code === 'INVALID_PATTERN') {
    return t('NLSEducationBuilderErrorInvalidPattern')
  }
  if (code === 'REASON_REQUIRED') {
    return t('NLSEducationBuilderErrorReasonRequired')
  }
  if (code === 'TOO_LATE_TO_EDIT') {
    return t('NLSEducationBuilderErrorTooLateToEdit')
  }
  if (code === 'NOT_YET') {
    return t('NLSEducationBuilderErrorNotYet')
  }
  if (code === 'SESSION_CANCELLED') {
    return t('NLSEducationBuilderErrorSessionCancelled')
  }
  if (code === 'NOT_ON_ROLL') {
    return t('NLSEducationBuilderErrorNotOnRoll')
  }
  if (code === 'HAS_SUBMISSIONS') {
    return t('NLSEducationBuilderErrorHasSubmissions')
  }
  if (code === 'ALREADY_APPROVED') {
    return t('NLSEducationBuilderErrorAlreadyApproved')
  }
  if (code === 'FEEDBACK_REQUIRED') {
    return t('NLSEducationBuilderErrorFeedbackRequired')
  }
  if (code === 'SCORE_NOT_ALLOWED') {
    return t('NLSEducationBuilderErrorScoreNotAllowed')
  }
  if (code === 'SCORE_OUT_OF_BOUNDS') {
    return t('NLSEducationBuilderErrorScoreOutOfBounds')
  }
  if (code === 'NOT_LEARNER_OR_GUARDIAN') {
    return t('NLSEducationBuilderErrorNotLearnerOrGuardian')
  }
  if (code === 'NOT_ACTIVE_MEMBER') {
    return t('NLSEducationBuilderErrorNotActiveMember')
  }
  if (code === 'ATTACHMENT_NOT_YOURS') {
    return t('NLSEducationBuilderErrorAttachmentNotYours')
  }
  if (code === 'INVALID_LEAVE_RANGE') {
    return t('NLSEducationBuilderErrorInvalidLeaveRange')
  }
  if (code === 'LEAVE_OVERLAP') {
    return t('NLSEducationBuilderErrorLeaveOverlap')
  }
  if (code === 'LEAVE_NOT_PENDING') {
    return t('NLSEducationBuilderErrorLeaveNotPending')
  }
  if (code === 'NOT_LEAVE_APPROVER') {
    return t('NLSEducationBuilderErrorNotLeaveApprover')
  }
  if (code === 'LEAVE_NOT_APPROVED') {
    return t('NLSEducationBuilderErrorLeaveNotApproved')
  }
  if (code === 'LEAVE_TOO_LARGE') {
    return t('NLSEducationBuilderErrorLeaveTooLarge')
  }
  if (code === 'LEARNER_ON_LEAVE') {
    return t('NLSEducationBuilderErrorLearnerOnLeave')
  }
  if (code === 'QUIZ_KEYS_LOCKED') {
    return t('NLSEducationWorkQuizErrorKeysLocked')
  }
  if (code === 'QUIZ_HAS_ATTEMPTS') {
    return t('NLSEducationWorkQuizErrorHasAttempts')
  }
  if (code === 'QUIZ_CLOSED') {
    return t('NLSEducationWorkQuizErrorClosed')
  }
  if (code === 'FORM_IN_USE') {
    return t('NLSEducationWorkQuizErrorFormInUse')
  }
  if (code === 'FORM_NOT_FOUND') {
    return t('NLSEducationWorkQuizErrorFormNotFound')
  }
  if (code === 'FORM_HAS_BINDINGS') {
    return t('NLSEducationWorkQuizErrorFormHasBindings')
  }
  if (code === 'FORM_HAS_FILE_FIELDS') {
    return t('NLSEducationWorkQuizErrorFormHasFileFields')
  }
  if (code === 'INVALID_PASS_PERCENT') {
    return t('NLSEducationWorkQuizErrorInvalidPassPercent')
  }
  if (code === 'INVALID_MAX_ATTEMPTS') {
    return t('NLSEducationWorkQuizErrorInvalidMaxAttempts')
  }
  if (code === 'KEYS_REQUIRED') {
    return t('NLSEducationWorkQuizErrorKeysRequired')
  }
  if (code === 'FIELD_NOT_FOUND') {
    return t('NLSEducationWorkQuizErrorFieldNotFound')
  }
  if (code === 'UNSCORABLE_FIELD_TYPE') {
    return t('NLSEducationWorkQuizErrorUnscorableFieldType')
  }
  if (code === 'INVALID_POINTS') {
    return t('NLSEducationWorkQuizErrorInvalidPoints')
  }
  if (code === 'INVALID_OPTION_ID') {
    return t('NLSEducationWorkQuizErrorInvalidOptionId')
  }
  if (code === 'INVALID_CORRECT_VALUE') {
    return t('NLSEducationWorkQuizErrorInvalidCorrectValue')
  }
  if (code === 'INVALID_TOLERANCE') {
    return t('NLSEducationWorkQuizErrorInvalidTolerance')
  }
  if (code === 'NOT_SUBJECT_NODE') {
    return t('NLSEducationWorkQuizErrorNotSubjectNode')
  }
  if (code === 'QUIZ_ATTEMPTS_USED') {
    return t('NLSEducationWorkQuizErrorAttemptsUsed')
  }
  if (code === 'KEYS_NOT_FOUND') {
    return t('NLSEducationWorkQuizErrorKeysNotFound')
  }
  if (code === 'CURRENCY_NOT_SET') {
    return t('NLSEducationFeesErrorCurrencyNotSet')
  }
  if (code === 'REFERENCE_REQUIRED') {
    return t('NLSEducationFeesErrReferenceRequired')
  }
  if (code === 'OVERPAYMENT') {
    return t('NLSEducationFeesErrOverpayment')
  }
  if (code === 'FUTURE_DATE') {
    return t('NLSEducationFeesErrFutureDate')
  }
  if (code === 'DUE_NOT_OPEN') {
    return t('NLSEducationFeesErrDueNotOpen')
  }
  if (code === 'WRONG_LEARNER') {
    return t('NLSEducationFeesErrWrongLearner')
  }
  if (code === 'BUDGET_KEY_MISSING') {
    return t('NLSEducationFeesErrBudgetKeyMissing')
  }
  if (code === 'EDIT_WINDOW_EXPIRED') {
    return t('NLSEduErrorEditWindowExpired')
  }
  if (code === 'DELETE_WINDOW_EXPIRED') {
    return t('NLSEduErrorDeleteWindowExpired')
  }
  if (code === 'MINOR_READ_ONLY') {
    return t('NLSEduErrorMinorReadOnly')
  }
  if (code === 'NOT_MESSAGEABLE_TEACHER') {
    return t('NLSEduErrorNotMessageableTeacher')
  }
  if (code === 'NOT_LEARNER_TEACHER') {
    return t('NLSEduErrorNotLearnerTeacher')
  }
  if (code === 'CANNOT_OPEN_FOR_OTHER_STAFF') {
    return t('NLSEduErrorCannotOpenForOtherStaff')
  }
  if (code === 'ADMIN_ONLY') {
    return t('NLSEduErrorAdminOnly')
  }
  if (code === 'NOT_CLASS_STAFF') {
    return t('NLSEduErrorNotClassStaff')
  }
  if (code === 'NOT_PROGRAMME_TRAINER') {
    return t('NLSEduErrorNotProgrammeTrainer')
  }
  if (code === 'ATTACHMENT_NOT_YOURS') {
    return t('NLSEduErrorAttachmentNotYours')
  }
  if (code === 'MESSAGING_OFF' || code === 'MESSAGING_DISABLED') {
    return t('NLSEduErrorMessagingOff')
  }
  if (code === 'ALREADY_HAD_TRIAL') {
    return t('NLSEduAdmissionsErrorAlreadyHadTrial')
  }
  if (code === 'CAP_REACHED' || code === 'CAPACITY_REACHED') {
    return t('NLSEduAdmissionsErrorCapReached')
  }
  if (code === 'FEE_PLAN_WRONG_ROOT') {
    return t('NLSEduAdmissionsErrorFeePlanWrongRoot')
  }
  if (code === 'FEE_PLAN_MISMATCH') {
    return t('NLSEduAdmissionsErrorFeePlanMismatch')
  }
  if (code === 'CONSENT_ATTESTATION_REQUIRED') {
    return t('NLSEduAdmissionsErrorConsentAttestationRequired')
  }
  if (code === 'ENQUIRY_ALREADY_ADMITTED') {
    return t('NLSEduAdmissionsErrorEnquiryAlreadyAdmitted')
  }
  if (code === 'ENQUIRY_CLOSED') {
    return t('NLSEduAdmissionsErrorEnquiryClosed')
  }
  if (code === 'invalid-argument') {
    return t('NLSEducationBuilderErrorInvalidArgument')
  }
  if (code === 'permission-denied') {
    return t('NLSEducationBuilderErrorPermissionDenied')
  }
  return t('NLSEducationBuilderErrorGeneric')
}

/**
 * Maps server errors from grading, assessments, marks, and locks callables to NLS messages.
 */
export function mapEduGradingError(
  err: unknown,
  t: (key: NlsKey, params?: NlsInterpolationValues) => string,
  terms?: readonly { id: string; name: string }[],
): string {
  const details = (
    err as {
      details?: {
        code?: string
        termIds?: string[]
        areaId?: string
        label?: string
        learnerId?: string
        subjectNodeId?: string
      }
    }
  )?.details
  const rawCode =
    typeof err === 'string'
      ? err
      : details?.code ||
        (err as { code?: string })?.code ||
        ''
  const code = rawCode.replace(/^functions\//, '')

  if (code === 'VERSION_CONFLICT' || code === 'SCHEME_VERSION_CONFLICT') {
    return t('NLSEducationGradingErrorVersionConflict')
  }
  if (code === 'SCHEME_LOCKED') {
    const lockedIds = details?.termIds
    if (Array.isArray(lockedIds) && lockedIds.length > 0 && terms && terms.length > 0) {
      const names = lockedIds
        .map((id) => terms.find((tm) => tm.id === id)?.name || id)
        .join(', ')
      return t('NLSEducationGradingErrorSchemeLockedWithTerms', { terms: names })
    }
    return t('NLSEducationGradingErrorSchemeLocked')
  }
  if (code === 'TERM_LOCKED') {
    return t('NLSEducationGradingErrorTermLocked')
  }
  if (code === 'IN_USE') {
    return t('NLSEducationGradingErrorInUse')
  }
  if (code === 'TERM_IN_USE') {
    return t('NLSEducationGradingErrorTermInUse')
  }
  if (code === 'CATEGORY_IN_USE') {
    return t('NLSEducationGradingErrorCategoryInUse')
  }
  if (code === 'AREA_IN_USE') {
    return details?.areaId
      ? t('NLSEducationGradingErrorAreaInUseNamed', { area: details.areaId })
      : t('NLSEducationGradingErrorAreaInUse')
  }
  if (code === 'SCALE_IN_USE') {
    return details?.label
      ? t('NLSEducationGradingErrorScaleInUseNamed', { label: details.label })
      : t('NLSEducationGradingErrorScaleInUse')
  }
  if (code === 'INVALID_CO_SCHOLASTIC') {
    return t('NLSEducationGradingErrorInvalidCoScholastic')
  }
  if (code === 'INVALID_CO_SCHOLASTIC_AREA') {
    return t('NLSEducationGradingErrorInvalidCoScholasticArea')
  }
  if (code === 'INVALID_CO_SCHOLASTIC_LABEL') {
    return t('NLSEducationGradingErrorInvalidCoScholasticLabel')
  }
  if (code === 'INVALID_SIGNATURE') {
    return t('NLSEducationReportErrorInvalidSignature')
  }
  if (code === 'INVALID_REPORT_CARD_SETTINGS') {
    return t('NLSEducationReportErrorInvalidSettings')
  }
  if (code === 'CARD_PUBLISHED') {
    return t('NLSEducationReportErrorCardPublished')
  }
  if (code === 'NOT_ON_SUBJECT_ROLL') {
    return t('NLSEducationReportErrorNotOnSubjectRoll')
  }
  if (code === 'TERM_NOT_LOCKED') {
    return t('NLSEducationReportErrorTermNotLocked')
  }
  if (code === 'INVALID_REMARKS') {
    return t('NLSEducationReportErrorInvalidRemarks')
  }
  if (code === 'SIGNER_MISMATCH') {
    return t('NLSEducationReportErrorSignerMismatch')
  }
  if (code === 'PDF_NOT_READY') {
    return t('NLSEducationReportErrorPdfNotReady')
  }
  if (code === 'INVALID_PRESET') {
    return t('NLSEducationGradingErrorInvalidPreset')
  }
  if (code === 'INVALID_PASS_PERCENT') {
    return t('NLSEducationGradingErrorInvalidPassPercent')
  }
  if (code === 'INVALID_TERMS') {
    return t('NLSEducationGradingErrorInvalidTerms')
  }
  if (code === 'INVALID_CATEGORIES') {
    return t('NLSEducationGradingErrorInvalidCategories')
  }
  if (code === 'CATEGORY_WEIGHTS_TOTAL') {
    return t('NLSEducationGradingErrorCategoryWeightsTotal')
  }
  if (code === 'INVALID_BANDS') {
    return t('NLSEducationGradingErrorInvalidBands')
  }
  if (code === 'ARCHIVED_YEAR') {
    return t('NLSEducationBuilderErrorArchivedYear')
  }
  if (code === 'MODULE_OFF') {
    return t('NLSEducationBuilderErrorModuleOff')
  }
  if (code === 'SCHEME_REQUIRED') {
    return t('NLSEducationGradingErrorSchemeRequired')
  }
  if (code === 'UNKNOWN_CATEGORY') {
    return t('NLSEducationGradingErrorUnknownCategory')
  }
  if (code === 'UNKNOWN_TERM') {
    return t('NLSEducationGradingErrorUnknownTerm')
  }
  if (code === 'TERM_REQUIRED') {
    return t('NLSEducationGradingErrorTermRequired')
  }
  if (code === 'CATEGORY_REQUIRED') {
    return t('NLSEducationGradingErrorCategoryRequired')
  }
  if (code === 'MAX_SCORE_REQUIRED') {
    return t('NLSEducationGradingErrorMaxScoreRequired')
  }
  if (code === 'SOURCE_MANAGED') {
    return t('NLSEducationGradingErrorSourceManaged')
  }
  if (code === 'ASSESSMENT_ARCHIVED' || code === 'ALREADY_ARCHIVED') {
    return t('NLSEducationGradingErrorAssessmentArchived')
  }
  if (code === 'MAX_BELOW_MARKS') {
    return t('NLSEducationGradingErrorMaxBelowMarks')
  }
  if (code === 'NOT_ON_ROLL') {
    return t('NLSEducationBuilderErrorNotOnRoll')
  }
  if (code === 'INVALID_SCORE') {
    return t('NLSEducationGradingErrorInvalidScore')
  }
  if (code === 'INVALID_TERM') {
    return t('NLSEducationGradingErrorInvalidTerm')
  }
  if (code === 'NO_ASSESSMENTS') {
    return t('NLSEducationGradingErrorNoAssessments')
  }
  if (code === 'ALREADY_LOCKED') {
    return t('NLSEducationGradingErrorAlreadyLocked')
  }
  if (code === 'NOT_LOCKED') {
    return t('NLSEducationGradingErrorNotLocked')
  }
  if (code === 'MARKS_INCOMPLETE') {
    return t('NLSEducationGradingErrorMarksIncomplete')
  }
  if (code === 'RECOMPUTE_PENDING') {
    return t('NLSEducationGradingErrorRecomputePending')
  }
  if (code === 'RECOMPUTE_INCOMPLETE') {
    return t('NLSEducationGradingErrorRecomputeIncomplete')
  }
  if (code === 'NOT_SUBJECT_NODE') {
    return t('NLSEducationWorkQuizErrorNotSubjectNode')
  }
  if (code === 'NOT_CLASS_NODE') {
    return t('NLSEducationGradingErrorNotClassNode')
  }
  if (code === 'NOT_AUTHORIZED_STAFF' || code === 'permission-denied') {
    return t('NLSEducationBuilderErrorPermissionDenied')
  }
  if (code === 'NODE_ACCESS_DENIED') {
    return t('NLSEducationGradingErrorNodeAccessDenied')
  }
  if (code === 'invalid-argument') {
    return t('NLSEducationBuilderErrorInvalidArgument')
  }

  return t('NLSEducationBuilderErrorGeneric')
}

export interface OfficeHoursWindow {
  day: number
  start: string
  end: string
}

export function validateOfficeHours(windows: OfficeHoursWindow[]): {
  valid: boolean
  errorKey?: 'NLSEducationOfficeHoursErrorInvalidTimes' | 'NLSEducationOfficeHoursErrorOverlap'
} {
  for (const w of windows) {
    if (!w.start || !w.end || w.start >= w.end) {
      return { valid: false, errorKey: 'NLSEducationOfficeHoursErrorInvalidTimes' }
    }
  }

  const byDay: Record<number, OfficeHoursWindow[]> = {}
  for (const w of windows) {
    byDay[w.day] = byDay[w.day] || []
    byDay[w.day].push(w)
  }

  for (const day in byDay) {
    const sorted = [...byDay[day]].sort((a, b) => a.start.localeCompare(b.start))
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i].end > sorted[i + 1].start) {
        return { valid: false, errorKey: 'NLSEducationOfficeHoursErrorOverlap' }
      }
    }
  }

  return { valid: true }
}

